import { z } from 'zod';
import { AuditSchema } from '../settings/audit-schema';
import { CatalogSectionSchema } from '../settings/catalog-base-schema';
import {
  ProductLayoutSchema,
  type ProductLayout,
} from '../settings/product-layout-schema';
export { ProductLayoutSchema, type ProductLayout };

export const ProductFAQSchema = z.object({
  question: z.string().min(5, 'Pregunta muy corta'),
  answer: z.string().min(5, 'Respuesta muy corta'),
});

export const ProductInputSchema = z.object({
  // --- BASE (Común a todos) ---
  name: z.string().min(2, 'El nombre es obligatorio'),
  price: z.number().min(0, 'El precio debe ser 0 o más').default(0),
  showPrice: z.boolean().default(true), // Control individual por producto
  description: z.string().optional().nullable(), // "Qué incluye" o descripción general
  showcase: CatalogSectionSchema.omit({
    id: true,
    order: true,
    links: true,
  })
    .extend({
      type: z.enum(['grid', 'carousel', 'comparison']).default('carousel'),
    })
    .optional(),

  previewVideoId: z.string().optional().nullable(),
  previewVideoLibraryId: z.string().optional().nullable(),

  // --- CONTROL DE EXPERIENCIA (NUEVO) ---
  layout: ProductLayoutSchema.default('standard'),
  customCTA: z.string().optional().nullable(), // Ej: "¡Pide tu Sello!"
  whatsappMessage: z.string().optional().nullable(), // Mensaje pre-llenado contextual
  redirectPhone: z.string().optional().nullable(), // Custom WhatsApp number
  uspBadges: z.array(z.string()).default([]), // Ej: ["Listo en 1h", "Garantizado"]

  // --- CAPA TÉCNICA (Rodarios / Industrial) ---
  sku: z.string().optional().nullable(),
  brand: z.string().optional().nullable(),
  specifications: z.record(z.string(), z.string()).optional().default({}), // { "Medida": "20mm" }
  stockStatus: z
    .enum(['in_stock', 'out_of_stock', 'on_demand'])
    .default('in_stock'),

  // --- SERVICIOS Y CURSOS ---
  requirements: z.array(z.string()).default([]), // Checklist de requisitos o pasos
  serviceSteps: z
    .array(
      z.object({
        title: z.string(),
        description: z.string().optional(),
      }),
    )
    .default([]), // Roadmap / Fases del servicio
  serviceLocation: z
    .enum(['office', 'home', 'remote', 'hybrid'])
    .default('office'),
  guarantee: z.string().optional().nullable(),
  deliveryTime: z.string().optional().nullable(), // Ej: "Entrega en 24h"
  duration: z.string().optional().nullable(), // Para cursos: "12 horas"
  startDate: z.string().optional().nullable(), // Para cursos: "15 de Mayo"
  instructor: z.string().optional().nullable(), // Para cursos: "Merli Hernández"

  // --- CAPA GASTRONÓMICA (Comida) ---
  ingredients: z.array(z.string()).default([]), // Para menú
  portionSize: z.string().optional().nullable(), // Ej: "Para 2 personas"

  // --- ORGANIZACIÓN Y VISIBILIDAD ---
  groupId: z.string().optional().nullable(), // Vinculado a productGroups
  isActive: z.boolean().default(true),
  isPopular: z.boolean().default(false),
  showInPublicPage: z.boolean().default(true),
  order: z.number().default(0),
  searchTags: z.array(z.string()).default([]),
  faqs: z.array(ProductFAQSchema).default([]),
  assignedPixelId: z.string().optional().nullable(),
});

export const ProductSchema = ProductInputSchema.extend({
  ownerId: z.string(),
  audit: AuditSchema,
});

export type Product = z.infer<typeof ProductSchema>;
export type ProductInput = z.infer<typeof ProductInputSchema>;
export type ProductWithId = Product & { id: string };
